import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { cityCenter, placeCategories, type Place } from '../../data/plan';
import { profileCoordinates } from '../../data/demandes';
interface CityMapProps {
  places: Place[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}
function pinIcon(place: Place, active: boolean): L.DivIcon {
  const color = placeCategories[place.category].color;
  const size = active ? 32 : 22;
  return L.divIcon({
    className: 'plan-pin',
    html: `<span style="background:${color};width:${size}px;height:${size}px" class="block rounded-full border-[3px] border-white shadow-md shadow-ville-navy/30"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
export function CityMap({
  places,
  selectedId,
  onSelect
}: CityMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center: cityCenter,
      zoom: 13,
      zoomControl: false,
      scrollWheelZoom: true,
      attributionControl: true
    });
    map.attributionControl.setPrefix(false);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(map);
    L.control.zoom({
      position: 'topright'
    }).addTo(map);
    L.circleMarker(profileCoordinates, {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: '#1a1a17',
      fillOpacity: 1
    }).bindTooltip('Votre adresse').addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    places.forEach(place => {
      L.marker([place.lat, place.lng], {
        icon: pinIcon(place, place.id === selectedId),
        keyboard: true,
        title: place.name,
        alt: `${placeCategories[place.category].label} — ${place.name}`,
        zIndexOffset: place.id === selectedId ? 500 : 0
      }).on('click', () => onSelect(place.id)).on('keypress', () => onSelect(place.id)).addTo(layer);
    });
  }, [places, selectedId, onSelect]);
  useEffect(() => {
    const place = places.find(item => item.id === selectedId);
    if (!mapRef.current || !place) return;
    mapRef.current.panTo([place.lat, place.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selectedId, places]);
  return <div ref={containerRef} role="application" aria-label="Plan de la ville de Brossard" className="h-full w-full bg-ville-mist" data-id="element-1789" data-mp="5x6n6izjal5c" />;
}